import { Input, PaperClip } from 'components';
import { BackIcon, UploadIcon, Download2Icon, TrashIcon } from 'assets/icons';
import { Globals } from 'utils';

import React, { useState } from 'react';

const getRequiredDocs = (documents: any) => {
  return documents.filter((el: any) => el.name !== 'Fé de vida');
};

// Límite de peso por archivo, alineado con el límite observado en la API de Apacuana.
const MAX_FILE_SIZE_MB = 6;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface UploadDocsCertificateData {
  cert: any;
  handleNext: (data?: any) => void;
  handleBack: () => void;
  apacuanaId: any;
  pendingDocuments: any;
  apacuanaUser: any;
}

const UploadDocsCertificate: React.FC<UploadDocsCertificateData> = ({
  cert,
  handleNext,
  handleBack,
  apacuanaId,
  pendingDocuments,
  apacuanaUser,
}) => {
  const allDocuments = cert?.documents || [];
  const requiredDocuments = getRequiredDocs(allDocuments);
  const [data, setData] = useState<any>({});
  const handleFileChange = (documentId: string) => (e: any) => {
    const file = e.target.files[0];

    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      Globals.showError(
        `${file.name} pesa ${sizeMb} MB y supera el límite permitido de ${MAX_FILE_SIZE_MB} MB. Por favor, seleccione un archivo más liviano.`,
      );
      // Limpiamos el input para permitir volver a seleccionar el mismo archivo
      // (u otro) sin que el navegador ignore el cambio por ser el mismo valor.
      e.target.value = '';
      return;
    }

    setData((prev: any) => ({
      ...prev,
      [`file-${documentId}`]: file,
    }));
  };

  const isAllFilesUploaded = () => {
    const submittedDocIds = new Set(pendingDocuments.map((doc: any) => doc.id));

    const countUploadedAndNeeded = requiredDocuments.reduce((count: number, doc: any) => {
      const fileKey = `file-${doc.id}`;

      const isAlreadyOnServer = !submittedDocIds.has(doc.id);

      const isNewUpload = data[fileKey] && data[fileKey] instanceof File;

      if (isAlreadyOnServer || isNewUpload) {
        return count + 1;
      }

      return count;
    }, 0);

    // Si el conteo coincide con el total de documentos requeridos, es válido.
    return countUploadedAndNeeded === requiredDocuments.length;
  };

  const isNextButtonDisabled = !isAllFilesUploaded();
  return (
    <div
      className='container-white'
      style={{
        padding: '25px',
        borderRadius: '8px',
        minWidth: 650,
      }}
    >
      <h3
        style={{
          fontSize: '1.4rem',
          fontWeight: 600,
          color: '#333',
          marginBottom: '15px',
          paddingBottom: '10px',
        }}
      >
        Información Personal
      </h3>
      <div
        style={{
          marginBottom: '25px',
          padding: '15px',
          borderLeft: '4px solid #007bff',
          backgroundColor: '#eef4ff',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <p
          style={{
            margin: 0,
            color: '#011E4A',
            fontSize: '0.95rem',
            fontWeight: 500,
          }}
        >
          Adjunte todos los documentos solicitados para continuar. Cada archivo debe pesar menos de{' '}
          {MAX_FILE_SIZE_MB} MB.
        </p>
      </div>

      {requiredDocuments.map((doc: any) => {
        let buttonContent = 'Subir archivo';
        let buttonColor = 'primary';
        let disabled = false;

        const docStatus = apacuanaUser?.documents?.find((p: any) => p.pref.id === doc.id)?.status;

        const currentStatusId = docStatus?.id;
        const uploadedFile = data[`file-${doc.id}`];
        if (currentStatusId === 1) {
          buttonContent = 'Archivo Aprobado';
          buttonColor = 'success';
          disabled = true;
        } else if (currentStatusId === 0) {
          buttonContent = 'En proceso de verificación';
          buttonColor = 'warning';
          disabled = true;
        } else if (currentStatusId === -1) {
          buttonContent = 'Vuelve a subir el archivo';
          buttonColor = 'error';
          disabled = false;
        }

        const buttonLabel = uploadedFile ? `Cambiar archivo (${uploadedFile.name})` : buttonContent;

        return (
          <div key={doc.id} className='col-12 col-sm-12' style={{ marginBottom: 8 }}>
            <div className='card shadow-sm h-100' style={{ borderRadius: '8px' }}>
              <div className='card-body'>
                <h5 className='card-title fw-bold' style={{ marginBottom: '0' }}>
                  {doc.name}
                </h5>

                <p
                  className='card-text text-secondary'
                  style={{ marginTop: '0.5rem', marginBottom: '1rem' }}
                >
                  {doc.dsc}
                </p>

                <label
                  style={{ borderColor: '#0777FF' }}
                  className={`btn btn${
                    buttonColor === 'error' ? 'danger' : 'primary'
                  } w-100 ${disabled ? 'disabled' : ''}`}
                  htmlFor={`file-upload-${doc.id}`}
                >
                  <img src={UploadIcon} style={{ maxWidth: 20, marginRight: 6 }} /> {buttonLabel}
                  <input
                    type='file'
                    id={`file-upload-${doc.id}`}
                    className='d-none'
                    onChange={handleFileChange(doc.id)}
                    disabled={disabled}
                    accept='application/pdf'
                  />
                </label>

                {uploadedFile && (
                  <p
                    className='text-secondary'
                    style={{
                      marginTop: '6px',
                      marginBottom: 0,
                      fontSize: '0.8rem',
                    }}
                  >
                    Tamaño: {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <div className='row' style={{ marginTop: '20px' }}>
        <div className='col-md-6'>
          <button
            className={`btn btn-report-2 ${''}`}
            onClick={() => handleBack()}
            style={{ width: '100%', background: '#011E4A !important' }}
          >
            <p>Regresar</p>
          </button>
        </div>

        <div className='col-md-6'>
          <button
            className={`btn btn-report ${isNextButtonDisabled ? 'disabled' : 'active'}`}
            onClick={() => {
              if (!isNextButtonDisabled) {
                handleNext(data);
              }
            }}
            disabled={isNextButtonDisabled}
            style={{ width: '100%' }}
          >
            <p>Siguiente</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadDocsCertificate;
